import * as React from 'react';
import { Toast as ToastPrimitive } from '@base-ui/react/toast';
import { cva } from 'class-variance-authority';

import { Button } from '@/ui/components/button';
import {
  CheckCircleIcon,
  CloseIcon,
  ErrorIcon,
  InfoIcon,
  WarningIcon,
} from '@/ui/icons';
import { cn } from '@/ui/lib/utils';

export type ToastType = 'info' | 'success' | 'warning' | 'danger';

const toast = ToastPrimitive.createToastManager();

function ToastProvider({ ...props }: ToastPrimitive.Provider.Props) {
  return <ToastPrimitive.Provider {...props} />;
}

function ToastPortal({ ...props }: ToastPrimitive.Portal.Props) {
  return <ToastPrimitive.Portal data-slot="toast-portal" {...props} />;
}

// Viewport: 右下 → 上部中央。bootstrap-notify と同じ上端 20px・幅（col-sm-4 相当の最大 360px）。
// z-index 1060 は Bootstrap 4 のモーダル（1050）より手前に出すための意図的な指定で、
// DS の「overlay に手動の z-index を付けない」ルールの例外。コンポーネントではなくこの viewport に持たせる。
// .ui-root は Portal が body 直下に出すため、島の境界をここに置く
function ToastViewport({ className, ...props }: ToastPrimitive.Viewport.Props) {
  return (
    <ToastPrimitive.Viewport
      data-slot="toast-viewport"
      className={cn(
        'ui-root tw:pointer-events-none tw:fixed tw:inset-x-4 tw:top-5 tw:z-[1060] tw:mx-auto tw:flex tw:w-auto tw:max-w-90 tw:flex-col tw:gap-2.5 tw:outline-none',
        className,
      )}
      {...props}
    />
  );
}

// Root: registry の重ね表示（absolute + transform）をやめ、古い順に縦に並べる（bootstrap-notify と同じ）。
// transition を付けないので、閉じると Base UI が次のフレームで DOM から外す（#18593 の 600ms 残りは解消）。
// leading-normal はレガシー CSS の body の line-height（2.2）を島の中で打ち消す
const toastVariants = cva(
  'tw:pointer-events-auto tw:relative tw:w-full tw:rounded-md tw:border tw:px-4 tw:py-3 tw:text-sm tw:leading-normal tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring',
  {
    variants: {
      type: {
        info: 'tw:border-alert-info-border tw:bg-alert-info tw:text-alert-info-foreground',
        success:
          'tw:border-alert-success-border tw:bg-alert-success tw:text-alert-success-foreground',
        warning:
          'tw:border-alert-warning-border tw:bg-alert-warning tw:text-alert-warning-foreground',
        danger:
          'tw:border-alert-danger-border tw:bg-alert-danger tw:text-alert-danger-foreground',
      },
    },
    defaultVariants: { type: 'info' },
  },
);

function Toast({
  className,
  toast: item,
  ...props
}: ToastPrimitive.Root.Props) {
  return (
    <ToastPrimitive.Root
      data-slot="toast"
      toast={item}
      className={cn(toastVariants({ type: item.type as ToastType }), className)}
      {...props}
    />
  );
}

// Content: 重ね表示用の data-behind での透明化と transition をやめる（縦に並べると古いものが消えるため）。
// padding は Root が持ち、右端は絶対配置の × の分だけ空ける
function ToastContent({ className, ...props }: ToastPrimitive.Content.Props) {
  return (
    <ToastPrimitive.Content
      data-slot="toast-content"
      className={cn('tw:flex tw:items-start tw:gap-3 tw:pr-8', className)}
      {...props}
    />
  );
}

// Title / Description: m-0 と leading-normal はレガシー CSS の h2・p の余白と行の高さを打ち消す
function ToastTitle({ className, ...props }: ToastPrimitive.Title.Props) {
  return (
    <ToastPrimitive.Title
      data-slot="toast-title"
      className={cn(
        'tw:m-0 tw:text-sm tw:leading-normal tw:font-medium',
        className,
      )}
      {...props}
    />
  );
}

// Description: 文字色は Root の種類別の色を継ぐ（registry の text-muted-foreground は外す）
function ToastDescription({
  className,
  ...props
}: ToastPrimitive.Description.Props) {
  return (
    <ToastPrimitive.Description
      data-slot="toast-description"
      className={cn('tw:m-0 tw:text-sm tw:leading-normal', className)}
      {...props}
    />
  );
}

function ToastAction({
  className,
  render = <Button variant="outline" size="sm" />,
  ...props
}: ToastPrimitive.Action.Props) {
  return (
    <ToastPrimitive.Action
      data-slot="toast-action"
      render={render}
      className={cn('tw:shrink-0', className)}
      {...props}
    />
  );
}

// Close: 読み上げ名は #18562 で決めた「メッセージを閉じる」。モーダルの × と区別するため。
// 色は link の青でなく toast の種類別の文字色を継ぐ（hover は link variant の opacity-70 のまま）。
// top-0 にすると Button sm（高さ 44px）の中心が 22px になり、本文 1 行目の中心（py-3 の 12px + 行の高さ 21px の半分 = 22.5px）とそろう。
// × は ToastIcon と同じ 20px にする。Button の svg の既定（24px）は size-* クラスを持つ svg には当たらないので、CloseIcon に size-5 を付ける
function ToastClose({
  className,
  children,
  ...props
}: ToastPrimitive.Close.Props) {
  return (
    <ToastPrimitive.Close
      data-slot="toast-close"
      aria-label="メッセージを閉じる"
      render={<Button variant="link" size="sm" />}
      className={cn(
        'tw:absolute tw:top-0 tw:right-1 tw:min-w-0 tw:px-2 tw:text-current',
        className,
      )}
      {...props}
    >
      {children ?? <CloseIcon aria-hidden="true" className="tw:size-5" />}
    </ToastPrimitive.Close>
  );
}

// ToastIcon: registry の lucide を @/ui/icons に置き換える。danger は registry の error に相当。
// 高さを本文 1 行分（leading-normal = 14px × 1.5 = 21px）にして、20px のアイコンをその中央に置く
function ToastIcon({ type }: { type: string | undefined }) {
  const Icon =
    type === 'success'
      ? CheckCircleIcon
      : type === 'info'
        ? InfoIcon
        : type === 'warning'
          ? WarningIcon
          : type === 'danger'
            ? ErrorIcon
            : null;
  if (!Icon) return null;
  return (
    <span
      data-slot="toast-icon"
      className="tw:flex tw:h-5.25 tw:shrink-0 tw:items-center tw:[&_svg]:pointer-events-none tw:[&_svg:not([class*=size-])]:size-5"
    >
      <Icon aria-hidden="true" />
    </span>
  );
}

// ToastList: Base UI の toasts は新しいものが先頭。bootstrap-notify と同じく古い順に上から並べる
function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager();
  return [...toasts].reverse().map(item => (
    <Toast key={item.id} toast={item}>
      <ToastContent>
        <ToastIcon type={item.type} />
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
          <ToastTitle />
          <ToastDescription />
        </div>
        <ToastClose />
      </ToastContent>
    </Toast>
  ));
}

// Provider の購読は effect で行われる。子の effect は親より先に走るため、effect の中で直接
// onReady を呼ぶと Provider がまだ購読していない。microtask に遅らせて、同じ commit の
// effect が全て終わってから知らせる。onReady が変わるたびに再び呼ぶので、呼び出し側は安定した関数を渡すこと
function ToastReadySignal({ onReady }: { onReady: () => void }) {
  React.useEffect(() => {
    let live = true;
    void Promise.resolve().then(() => {
      if (live) onReady();
    });
    return () => {
      live = false;
    };
  }, [onReady]);
  return null;
}

// 表示数の上限なし（bootstrap-notify と同じ）。timeout は Base UI 既定の 5000 ms
function Toaster({
  children,
  toastManager = toast,
  ...props
}: ToastPrimitive.Provider.Props) {
  return (
    <ToastProvider
      toastManager={toastManager}
      limit={Number.MAX_SAFE_INTEGER}
      {...props}
    >
      {children}
      <ToastPortal>
        <ToastViewport>
          <ToastList />
        </ToastViewport>
      </ToastPortal>
    </ToastProvider>
  );
}

const createToastManager = ToastPrimitive.createToastManager;
const useToastManager = ToastPrimitive.useToastManager;

export {
  Toaster,
  Toast,
  ToastAction,
  ToastClose,
  ToastContent,
  ToastDescription,
  ToastPortal,
  ToastProvider,
  ToastReadySignal,
  ToastTitle,
  ToastViewport,
  createToastManager,
  toast,
  useToastManager,
};
